import * as React from "react";
import { Box, Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface FilmStripScrollerProps {
  images: string[];
  height?: number;
  speed?: number; // seconds per loop
}

export default function FilmStripScroller({
  images,
  height = 140,
  speed = 30,
}: FilmStripScrollerProps) {
  const [paused, setPaused] = React.useState(false);
  const [openImage, setOpenImage] = React.useState<string | null>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [scrollDistance, setScrollDistance] = React.useState<number>(0);
  const [loadedCount, setLoadedCount] = React.useState(0);

  // Slightly reduce height on very small screens to avoid large empty areas
  const mobileHeight = Math.max(80, Math.round(height * 0.75));

  // Measure after images load and on resize
  React.useEffect(() => {
    function measure() {
      if (trackRef.current) {
        const totalWidth = trackRef.current.scrollWidth;
        setScrollDistance(totalWidth / 2);
      }
    }

    // Only measure once all original images have loaded
    if (loadedCount >= images.length) {
      measure();
    }

    const ro = new ResizeObserver(() => measure());
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [loadedCount, images.length, height]);

  const loopImages = [...images, ...images];

  return (
    <>
      <Box
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        sx={{
          position: "relative",
          overflow: "hidden",
          width: "100%",
          height: { xs: mobileHeight, md: height },
          bgcolor: "#111",
        }}
      >
        {/* film holes top */}
        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: 0,
            right: 0,
            height: 10,
            background:
              "repeating-linear-gradient(90deg, #fff 0 8px, transparent 8px 20px)",
            zIndex: 2,
          }}
        />

        {/* film holes bottom */}
        <Box
          sx={{
            position: "absolute",
            bottom: 6,
            left: 0,
            right: 0,
            height: 10,
            background:
              "repeating-linear-gradient(90deg, #fff 0 8px, transparent 8px 20px)",
            zIndex: 2,
          }}
        />

        {/* scrolling track */}
        <Box
          ref={trackRef}
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            gap: 2,
            px: 3,
            animation: `film-scroll ${speed}s linear infinite`,
            animationPlayState: paused ? "paused" : "running",
            "@keyframes film-scroll": {
              from: { transform: "translateX(0)" },
              to: {
                transform: `translateX(-${scrollDistance}px)`,
              },
            },
          }}
        >
          {loopImages.map((src, i) => (
            <Box
              key={i}
              onClick={() => setOpenImage(src)}
              sx={{
                width: "auto",
                height,
                flexShrink: 0,
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "rgba(0,0,0,0.2)",
                cursor: "pointer",
                transition: "transform 0.25s ease",
                "&:hover": {
                  transform: "scale(1.08)",
                  zIndex: 3,
                },
              }}
            >
              <Box
                component="img"
                src={src}
                alt=""
                onLoad={() => setLoadedCount((c) => c + 1)}
                sx={{
                  height: "100%",
                  width: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Image dialog */}
      <Dialog
        open={Boolean(openImage)}
        onClose={() => setOpenImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            bgcolor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setOpenImage(null)}
            sx={{
              position: "absolute",
              top: -40,
              right: -40,
              color: "#fff",
            }}
          >
            <CloseIcon />
          </IconButton>

          {openImage && (
            <Box
              component="img"
              src={openImage}
              alt=""
              sx={{
                maxWidth: "90vw",
                maxHeight: "80vh",
                borderRadius: 2,
                display: "block",
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
}
